import React from 'react';
import { IDroneContext } from './types';
import { ArcSocketContext, ChannelResponseEnum } from '@src/core/socket';
import { useGetDronesQuery } from '@src/core/features/drone/redux';
import { DroneStatusType } from '@src/core/features/drone/entities';

export const DroneContext = React.createContext<IDroneContext>({
  data: [],
  isLoading: false,
  refetch: () => {}
});


const EMPTY_DRONES = { data: [] };

interface DroneProviderProps {
  statuses?: DroneStatusType[];
}

export const DroneProvider: React.FC<React.PropsWithChildren<DroneProviderProps>> = ({ statuses, children }) => {
  const { subscribe, unsubscribe } = React.useContext(ArcSocketContext);
  const queryArg = React.useMemo(
    () => (statuses && statuses.length > 0 ? { status: statuses.join(',') } : undefined),
    [statuses]
  );
  // Refetch on mount / when the status filter changes (e.g. navigating between the all-drones
  // list and a selected drone) so the list reflects the latest state immediately instead of
  // showing cached data until the next WS push. Data persists during the refetch (no flicker).
  const { data = EMPTY_DRONES, refetch, isLoading, isUninitialized } = useGetDronesQuery(queryArg, {
    refetchOnMountOrArgChange: true,
  });

  const safeRefetch = React.useCallback(() => {
    if (isUninitialized) return;
    try {
      refetch();
    } catch {
      // RTK may throw "Cannot refetch a query that has not been started yet" during subscription churn
    }
  }, [isUninitialized, refetch]);

  React.useEffect(() => {
    subscribe(ChannelResponseEnum.UPDATE_DRONES, () => safeRefetch());
    return () => unsubscribe(ChannelResponseEnum.UPDATE_DRONES);
  }, [safeRefetch, subscribe, unsubscribe]);

  const value = React.useMemo(() => ({
    refetch: safeRefetch,
    isLoading,
    data: data.data,
  }), [safeRefetch, isLoading, data]);

  return (
    <DroneContext.Provider value={value}>
      {children}
    </DroneContext.Provider>
  );
};